import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import LayoutContainer from "../../LayoutContainer";
import styles from "./organizationDashboardDetails.module.css";
import { useHistory, useLocation } from "react-router-dom";
import SimpleBackIcon from "../../../assets/simpleBackIcon.svg";
import DashboardFilters from "../Helpers/Filters/DashboardFilters";
import { useOrgDashboardContext } from "../../../contexts/dashboard-context/OrgDashboardContext";
import {
  useOrgScope,
  serializeFromOrganization,
} from "../../../contexts/dashboard-context/OrgScopeContext";
import DatePickerModal from "../../DatePicker";
import { AuthContext } from "../../../contexts/auth-context";
import { useAsyncFn } from "../../../hooks/use-async-fn";
import DetailsPageChartCard from "../Helpers/Charts/DetailsPageChartCard";
import { formatCount } from "../index";
import DataTable, { DataTableRow } from "../Helpers/DataTable";
import config from "../../../config";
import {
  ORGANIZATION_DASHBOARD_BASE,
  buildDetailsApiQueryParams,
  getAdserverTableGroupKey,
  getDetailsPageTableColumns,
  getOrganizationDetailsConfig,
  getOrganizationDetailsFilters,
  initialFilterValuesForFilters,
} from "./organizationDashboardDetailsConfig";
import DrillDownDrawer, {
  DrillDownTarget,
  DrillEndpoint,
  MonthOption,
} from "../Helpers/DrillDownDrawer";

type DetailApiRow = {
  month: number;
  month_name: string;
  year: number;
  event_type: string;
  total_count?: number;
  query_count?: number;
  federation_id?: string;
  federation_name?: string;
  organization_id?: string;
  organization_name?: string;
  destination_adserver_type?: string;
};

type DetailDayApiRow = {
  date: string;
  event_type: string;
  total_count?: number;
  query_count?: number;
  federation_id?: string;
  federation_name?: string;
  organization_id?: string;
  organization_name?: string;
  destination_adserver_type?: string;
};

type ChartPoint = {
  date: string;
  query: number;
  engaged: number;
  audienceTransferred: number;
};

type FederationMetricKey = "total_count" | "query_count";

const toLocalDateOnly = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 3);
  return {
    start: toLocalDateOnly(start),
    end: toLocalDateOnly(end),
  };
};

type OrganizationDashboardLocationState = {
  dateRange?: { start?: string; end?: string };
  selectedEventType?: string;
};

const getDateRangeFromLocationState = (
  state: OrganizationDashboardLocationState | undefined,
) => {
  const start = state?.dateRange?.start;
  const end = state?.dateRange?.end;
  if (start && end) {
    return { start, end };
  }
  return null;
};

const getInitialFilterValues = (
  filters: ReturnType<typeof getOrganizationDetailsFilters>,
  state: OrganizationDashboardLocationState | undefined,
) => {
  const next = initialFilterValuesForFilters(filters);
  const hasEventTypeFilter = filters.some((filter) => filter.id === "event_type");
  if (hasEventTypeFilter && state?.selectedEventType) {
    next.event_type = state.selectedEventType;
  }
  return next;
};

const formatToDateOnly = (value: string | Date) => {
  if (typeof value === "string") {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  return toLocalDateOnly(d);
};

const toDatetimeLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const buildChartDataForMetric = (
  data: DetailApiRow[] | undefined,
  metricKey: FederationMetricKey,
): ChartPoint[] | null => {
  if (!data?.length) {
    return null;
  }
  const sortKeys = new Map<string, number>();
  const points = data.reduce<ChartPoint[]>((acc, item) => {
    const date = `${item.month_name} ${String(item.year).slice(-2)}`;
    const chartPoint = acc.find((point) => point.date === date);

    const key =
      item.event_type === "query"
        ? "query"
        : item.event_type === "marked_engaged"
          ? "engaged"
          : item.event_type === "audience_transferred"
            ? "audienceTransferred"
            : null;

    if (!key) {
      return acc;
    }

    sortKeys.set(date, item.year * 12 + item.month);
    const metricValue = item[metricKey] ?? 0;

    if (chartPoint) {
      chartPoint[key] += metricValue;
    } else {
      acc.push({
        date,
        query: key === "query" ? metricValue : 0,
        engaged: key === "engaged" ? metricValue : 0,
        audienceTransferred:
          key === "audienceTransferred" ? metricValue : 0,
      });
    }

    return acc;
  }, []);

  return points.sort(
    (a, b) => (sortKeys.get(a.date) ?? 0) - (sortKeys.get(b.date) ?? 0),
  );
};

const OrganizationDashboardDetails: React.FC = () => {
  const location = useLocation<OrganizationDashboardLocationState | undefined>();
  const { pathname } = location;
  const pageConfig = useMemo(
    () => getOrganizationDetailsConfig(pathname),
    [pathname],
  );

  const { appMetadata, orgGetFederationDetails, orgGetOrganizationDetails, orgGetAdserverDetails, orgTableGetFederationMonthLevelCount, orgTableGetOrganizationMonthLevelCount, orgTableGetAdserverMonthLevelCount, orgTableGetFederationDayLevelCount, orgTableGetOrganizationDayLevelCount, orgTableGetAdserverDayLevelCount } = useContext(AuthContext);

  const authRole =
    (appMetadata && (Object.values(appMetadata) as any[])[0]?.roles?.[0]) || "";
  const isAdmin =
    authRole === config.superUserRole ||
    authRole === config.applicationAdminRole;

  const activeFilters = useMemo(
    () => getOrganizationDetailsFilters(pageConfig, isAdmin),
    [pageConfig, isAdmin],
  );

  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    getInitialFilterValues(activeFilters, location.state),
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    return getDateRangeFromLocationState(location.state) ?? defaultDateRange();
  });
  const history = useHistory();
  const { dashboardData } = useOrgDashboardContext();
  const { orgId, fromOrganization } = useOrgScope();

  const [drillTarget, setDrillTarget] = useState<DrillDownTarget | null>(null);

  const deferFn =
    pageConfig.variant === "organization"
      ? orgGetOrganizationDetails
      : pageConfig.variant === "adserver"
        ? orgGetAdserverDetails
        : orgGetFederationDetails;

  const { run } = useAsyncFn({ deferFn });

  const [detailsResponse, setDetailsResponse] = useState<{
    data?: DetailApiRow[];
  } | null>(null);
  const [totalCountChartData, setTotalCountChartData] = useState<
    ChartPoint[] | null
  >(null);
  const [queryCountChartData, setQueryCountChartData] = useState<
    ChartPoint[] | null
  >(null);
  const [tableData, setTableData] = useState<DataTableRow[] | null>(null);
  const [selectedTableMonth, setSelectedTableMonth] = useState<string | null>(
    null,
  );
  const [tableDayRows, setTableDayRows] = useState<DataTableRow[]>([]);
  const [tableDayLoading, setTableDayLoading] = useState(false);

  useEffect(() => {
    setFilterValues((prev) => {
      const next = getInitialFilterValues(activeFilters, location.state);
      if (JSON.stringify(prev) === JSON.stringify(next)) {
        return prev;
      }
      return next;
    });
  }, [activeFilters, location.state]);

  const apiQueryParams = useMemo(() => {
    return buildDetailsApiQueryParams(activeFilters, filterValues);
  }, [activeFilters, filterValues]);

  const detailsRequestIdRef = useRef(0);

  const fetchDetails = useCallback(
    async (
      start: string,
      end: string,
      filters: Record<string, string | number>,
    ) => {
      const requestId = ++detailsRequestIdRef.current;
      setTotalCountChartData(null);
      setQueryCountChartData(null);
      try {
        const response = await run({
          org_id: orgId,
          start_date: start,
          end_date: end,
          from_organization: serializeFromOrganization(fromOrganization),
          ...filters,
        });
        if (detailsRequestIdRef.current === requestId) {
          setDetailsResponse(response);
        }
      } catch (error) {
        console.error("Failed to fetch organization details", error);
      }
    },
    [run, orgId, fromOrganization],
  );

  useEffect(() => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    fetchDetails(dateRange.start, dateRange.end, apiQueryParams);
  }, [dateRange.start, dateRange.end, apiQueryParams, fetchDetails, orgId, fromOrganization]);

  const handleDateApply = (start: string, end: string) => {
    setDateRange({
      start: formatToDateOnly(start),
      end: formatToDateOnly(end),
    });
  };

  const formatDateLabel = () => {
    if (!dateRange.start || !dateRange.end) return "Select date range";
    const fmt = (dateOnly: string) => {
      const [y, m, day] = dateOnly.split("-");
      return `${day}/${m}/${y}`;
    };
    return `${fmt(dateRange.start)} - ${fmt(dateRange.end)}`;
  };

  const { pickerInitialStart, pickerInitialEnd } = useMemo(() => {
    if (dateRange.start && dateRange.end) {
      return {
        pickerInitialStart: `${dateRange.start}T00:00`,
        pickerInitialEnd: `${dateRange.end}T23:59`,
      };
    }
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 3);
    start.setHours(0, 0, 0, 0);
    return {
      pickerInitialStart: toDatetimeLocal(start),
      pickerInitialEnd: toDatetimeLocal(end),
    };
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    const rows = detailsResponse?.data;
    if (rows && rows.length) {
      setTotalCountChartData(buildChartDataForMetric(rows, "total_count"));
      setQueryCountChartData(buildChartDataForMetric(rows, "query_count"));
    } else if (detailsResponse) {
      setTotalCountChartData([]);
      setQueryCountChartData([]);
    }
  }, [detailsResponse]);

  const tableMonthOptions = useMemo(
    () =>
      Array.from(
        new Set((tableData ?? []).map((r) => String(r.timePeriodMonth))),
      ),
    [tableData],
  );

  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;
    const tableMonthApiFn =
      pageConfig.variant === "organization"
        ? orgTableGetOrganizationMonthLevelCount
        : pageConfig.variant === "adserver"
          ? orgTableGetAdserverMonthLevelCount
          : orgTableGetFederationMonthLevelCount;
    const controller = new AbortController();
    setTableData(null);
    tableMonthApiFn({
      args: {
        org_id: orgId,
        start_date: dateRange.start,
        end_date: dateRange.end,
        from_organization: serializeFromOrganization(fromOrganization),
        ...apiQueryParams,
      },
      signal: controller.signal,
    })
      .then((response: { data?: DetailApiRow[] }) => {
        if (!controller.signal.aborted) {
          const rows = response?.data ?? [];
          setTableData(
            rows.map((item, index) => ({
              id: `${item.federation_id ?? item.organization_id ?? item.destination_adserver_type ?? "row"}-${item.month_name} ${item.year}-${item.event_type}-${index}`,
              entityId: item.federation_name ?? item.organization_name ?? "-",
              adserverType: item.destination_adserver_type ?? "-",
              timePeriodMonth: `${item.month_name} ${item.year}`,
              eventType: item.event_type ?? "-",
              totalCount: item.total_count ?? 0,
              queryCount: item.query_count ?? 0,
            })),
          );
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setTableData([]);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end, apiQueryParams, pageConfig.variant, orgId, fromOrganization]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedTableMonth) {
      setTableDayRows([]);
      setTableDayLoading(false);
      return;
    }
    const parsed = new Date(`${selectedTableMonth} 1 00:00:00`);
    if (Number.isNaN(parsed.getTime())) return;
    const { startDate, endDate } = buildMonthRange(
      parsed.getFullYear(),
      parsed.getMonth(),
    );
    const dayApiFn =
      pageConfig.variant === "organization"
        ? orgTableGetOrganizationDayLevelCount
        : pageConfig.variant === "adserver"
          ? orgTableGetAdserverDayLevelCount
          : orgTableGetFederationDayLevelCount;
    const controller = new AbortController();
    setTableDayLoading(true);
    setTableDayRows([]);
    dayApiFn({
      args: {
        org_id: orgId,
        start_date: startDate,
        end_date: endDate,
        from_organization: serializeFromOrganization(fromOrganization),
        ...apiQueryParams,
      },
      signal: controller.signal,
    })
      .then((res: { data?: DetailDayApiRow[] }) => {
        if (!controller.signal.aborted) {
          const data = res?.data ?? [];
          setTableDayRows(
            data.map((item, index) => ({
              id: `day-${item.federation_id ?? item.organization_id ?? item.destination_adserver_type ?? "row"}-${item.date}-${item.event_type}-${index}`,
              entityId: item.federation_name ?? item.organization_name ?? "-",
              adserverType: item.destination_adserver_type ?? "-",
              date: item.date
                ? (() => {
                    const d = new Date(`${item.date}T00:00:00`);
                    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                  })()
                : "-",
              eventType: item.event_type ?? "-",
              totalCount: item.total_count ?? 0,
              queryCount: item.query_count ?? 0,
            })),
          );
          setTableDayLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setTableDayLoading(false);
      });
    return () => controller.abort();
  }, [
    selectedTableMonth,
    pageConfig.variant,
    apiQueryParams,
    orgId,
    fromOrganization,
    orgTableGetFederationDayLevelCount,
    orgTableGetOrganizationDayLevelCount,
    orgTableGetAdserverDayLevelCount,
  ]);

  const tableTimeGranularity = selectedTableMonth ? "day" : "month";

  const tableColumns = useMemo(
    () => getDetailsPageTableColumns(pageConfig.variant, tableTimeGranularity),
    [pageConfig.variant, tableTimeGranularity],
  );

  const tableGroupByKey =
    pageConfig.variant === "adserver"
      ? getAdserverTableGroupKey(tableTimeGranularity)
      : undefined;

  const displayTableRows = selectedTableMonth
    ? tableDayRows
    : (tableData ?? []);

  const isTotalCountChartReady = Array.isArray(totalCountChartData);
  const isQueryCountChartReady = Array.isArray(queryCountChartData);
  const isTableDataReady = Array.isArray(tableData);

  const tableLoading = selectedTableMonth ? tableDayLoading : !isTableDataReady;

  const setFilter = (filterId: string) => (value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: value === "ALL" ? "" : value,
    }));
  };

  const clearAllFilters = () => {
    setFilterValues(initialFilterValuesForFilters(activeFilters));
    setSelectedTableMonth(null);
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 3);
    setDateRange({
      start: toLocalDateOnly(start),
      end: toLocalDateOnly(end),
    });
  };
  // Build start/end ISO dates for a given month index + year.
  // Uses local date parts (not toISOString) to avoid UTC timezone shift.
  const buildMonthRange = (
    year: number,
    monthIdx: number,
  ): { startDate: string; endDate: string } => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return {
      startDate: fmt(new Date(year, monthIdx, 1)),
      endDate: fmt(new Date(year, monthIdx + 1, 0)),
    };
  };
  // Derive the list of months covered by the current date range
  const availableMonths = (): MonthOption[] => {
    if (!dateRange.start || !dateRange.end) return [];
    const months: MonthOption[] = [];
    // Parse as local time (append T00:00:00) to avoid UTC date shift
    const start = new Date(dateRange.start + "T00:00:00");
    const end = new Date(dateRange.end + "T00:00:00");
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const { startDate, endDate } = buildMonthRange(
        cur.getFullYear(),
        cur.getMonth(),
      );
      months.push({
        label: cur.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        startDate,
        endDate,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  };

  const openDrill = (
    cardTitle: string,
    eventType?: string | null,
    monthLabelStr?: string,
    endpoint?: DrillEndpoint,
    entityParams?: Record<string, string | number>,
    chart?: Pick<
      DrillDownTarget,
      "chartLinesByEventType" | "dayLevelChartMetric" | "allowedDimensionKeys"
    >,
  ) => {
    const months = availableMonths();
    if (monthLabelStr) {
      const fallbackStartDate = months[months.length - 1]?.startDate;
      let parsedStartDate: string | undefined;

      if (/^\d{4}-\d{2}-\d{2}$/.test(monthLabelStr)) {
        const [yearStr, monthStr] = monthLabelStr.split("-");
        const year = Number(yearStr);
        const monthIdx = Number(monthStr) - 1;
        if (!Number.isNaN(year) && monthIdx >= 0 && monthIdx <= 11) {
          parsedStartDate = buildMonthRange(year, monthIdx).startDate;
        }
      } else {
        // Handle chart labels like "Jan 24" or "Jan 2024".
        const monthMatch = monthLabelStr.match(
          /^([A-Za-z]{3})\s+(\d{2}|\d{4})$/,
        );
        if (monthMatch) {
          const monthMap: Record<string, number> = {
            Jan: 0,
            Feb: 1,
            Mar: 2,
            Apr: 3,
            May: 4,
            Jun: 5,
            Jul: 6,
            Aug: 7,
            Sep: 8,
            Oct: 9,
            Nov: 10,
            Dec: 11,
          };
          const monthIdx = monthMap[monthMatch[1]];
          const rawYear = Number(monthMatch[2]);
          const year = monthMatch[2].length === 2 ? 2000 + rawYear : rawYear;
          if (monthIdx !== undefined && !Number.isNaN(year)) {
            parsedStartDate = buildMonthRange(year, monthIdx).startDate;
          }
        }
      }
      const startDate = parsedStartDate ?? fallbackStartDate;
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        defaultMonthStartDate: startDate,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chart,
      });
    } else {
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chart,
      });
    }
  };

  const LINE_KEY_TO_EVENT_TYPE: Record<string, string> = {
    query: "query",
    engaged: "marked_engaged",
    audienceTransferred: "audience_transferred",
  };

  const chartLines = useMemo(() => {
    if (pageConfig.variant === "adserver" || !isAdmin) {
      return [
        {
          key: "audienceTransferred",
          name: "Audience Transferred",
          color: "#F59E0B",
        },
      ];
    }
    return [
      { key: "query", name: "Query", color: "#0D6AB3" },
      { key: "engaged", name: "Engaged", color: "#2ACDFF" },
      {
        key: "audienceTransferred",
        name: "Audience Transferred",
        color: "#F59E0B",
      },
    ];
  }, [pageConfig.variant, isAdmin]);

  const toAllowedEventTypes = (visibleKeys: string[]): string[] | undefined => {
    const mapped = visibleKeys
      .map((k) => LINE_KEY_TO_EVENT_TYPE[k] ?? k)
      .filter(Boolean);
    return mapped.length > 0 ? mapped : undefined;
  };

  return (
    <LayoutContainer>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <button
            className={styles.backBtn}
            onClick={() => history.push(ORGANIZATION_DASHBOARD_BASE)}
          >
            <img src={SimpleBackIcon} alt="back" />
            <span>Back</span>
          </button>
          <h1 className={styles.pageTitle}>{pageConfig.pageTitle}</h1>
          <div className={styles.pageHeaderSpacer} />
        </div>

        <div className={styles.container}>
          <div className={styles.federationSummary}>
            <h3 className={styles.federationSummaryTitle}>
              {pageConfig.sectionTitle}
            </h3>
            <div className={styles.topBarRight}>
              <button
                className={styles.clearFiltersLink}
                onClick={clearAllFilters}
              >
                Clear filters
              </button>
              <button
                className={styles.dateRangeBtn}
                onClick={() => setDatePickerOpen(true)}
                type="button"
              >
                <span>{formatDateLabel()}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM9 14H7V12H9V14ZM13 14H11V12H13V14ZM17 14H15V12H17V14ZM9 18H7V16H9V18ZM13 18H11V16H13V18ZM17 18H15V16H17V18Z"
                    fill="var(--Primary-Color-Shade-1)"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.filtersRow}>
            {activeFilters.map((currentFilter) => (
              <DashboardFilters
                key={currentFilter.id}
                value={filterValues[currentFilter.id] ?? ""}
                options={currentFilter.getOptions(dashboardData)}
                onChange={
                  currentFilter.disabled
                    ? () => {}
                    : setFilter(currentFilter.id)
                }
                selectName={currentFilter.selectName}
                disabled={currentFilter.disabled}
                style={currentFilter.style}
                preserveOptionCase={currentFilter.preserveOptionCase}
              />
            ))}
          </div>

          <div className={styles.chartsGrid}>
            <div className={styles.chartContainer}>
              <DetailsPageChartCard
                title="Event Count"
                xAxisKey="date"
                data={queryCountChartData ?? []}
                yTickFormatter={formatCount}
                isLoading={!isQueryCountChartReady}
                lines={chartLines}
                onMonthClick={(label, visibleKeys) =>
                  openDrill(
                    pageConfig.variant === "federation"
                      ? "Federation wise event of count summary"
                      : pageConfig.variant === "organization"
                        ? "Organization Event Summary"
                        : "Ad Server wise event of count summary",
                    filterValues.event_type ?? "",
                    label,
                    (pageConfig.variant === "federation"
                      ? "federation"
                      : pageConfig.variant === "organization"
                        ? "organization"
                        : "adserver") as DrillEndpoint,
                    apiQueryParams,
                    {
                      chartLinesByEventType: true,
                      dayLevelChartMetric: "query_count",
                      allowedDimensionKeys: toAllowedEventTypes(visibleKeys),
                    },
                  )
                }
              />
            </div>
            <div className={styles.chartContainer}>
              <DetailsPageChartCard
                title="Audience Volume"
                xAxisKey="date"
                data={totalCountChartData ?? []}
                yTickFormatter={formatCount}
                isLoading={!isTotalCountChartReady}
                lines={chartLines}
                onMonthClick={(label, visibleKeys) =>
                  openDrill(
                    pageConfig.variant === "federation"
                      ? "Federation wise audience Count summary"
                      : pageConfig.variant === "organization"
                        ? "Organization wise audience Count summary"
                        : "Ad Server wise audience Count summary",
                    filterValues.event_type ?? "",
                    label,
                    (pageConfig.variant === "federation"
                      ? "federation"
                      : pageConfig.variant === "organization"
                        ? "organization"
                        : "adserver") as DrillEndpoint,
                    apiQueryParams,
                    {
                      chartLinesByEventType: true,
                      dayLevelChartMetric: "total_count",
                      allowedDimensionKeys: toAllowedEventTypes(visibleKeys),
                    },
                  )
                }
              />
            </div>
          </div>
          <div className={styles.tableContainer}>
            <DataTable
              title={pageConfig.tableTitle}
              columns={tableColumns}
              rows={displayTableRows}
              isLoading={tableLoading}
              groupByKey={tableGroupByKey}
              monthFilter={{
                options: tableMonthOptions,
                selected: selectedTableMonth,
                onChange: setSelectedTableMonth,
              }}
            />
          </div>
        </div>
      </div>
      <DatePickerModal
        datePickerOpen={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onApply={handleDateApply}
        initialStart={pickerInitialStart}
        initialEnd={pickerInitialEnd}
      />
      <DrillDownDrawer
        target={drillTarget}
        onClose={() => setDrillTarget(null)}
      />
    </LayoutContainer>
  );
};

export default OrganizationDashboardDetails;
